import Config from 'config/settings';
import { DepartmentType } from 'app/data/Enums';
import ComboCounter from 'utils/ComboCounter';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import { WorkController } from 'app/views/work/WorkController';
import fastdom from 'fastdom';
import { UIController } from 'app/views/ui/UIController';
import { device } from 'utils/device';

/**
 * Handle all CSS style state switches and timers on transitions;
 */
export default {
	historyStateSpeedScale: 0.8,
	normalSpeedScale: 1,
	speedScale: 1,
	$body: null,
	$overlayBg: null,
	$home: null,
	$circleCloseProgess: null,
	$projectContent: null,
	$projectDynamicContent: null,
	$projectContainer: null,
	DOM: null,
	comboCounter: new ComboCounter(),
	oldWorkController: null,
	currentWorkController: null,
	homeController: null,
	spacesController: null,
	screensController: null,
	systemsController: null,
	projectController: null,
	uiController: null,
	scrollbarController: null,
	focusedProjectUiTheme: 'light',
	projectIsHiddenCallback: null,
	onStartProjectToWorkSwitch: null,
	onWorkZoomBackComplete: null,

	tweens: {
		slideInProjectContent: null,
		fadeInProjectVideo: null,
		fadeOutProjectVideo: null,
		tileContainerZoomToProject: null,

		//Work <> Project tweens
		workTileUiTitle: null,
		workTileUiCircle: null,
		workTileUiSubtitle: null,
		workTileImage: null,
		workTileImageRadius: null,
		workTileMask: null,
		workTileSupportElements: null,
		workTileBlackout: null,
		workTileParticles: [],
		otherWorkTiles: [],

		projectLoader: null
	},

	tick() {
		this.comboCounter.tick();
	},

	clear() {
		console.log('SiteTransitions.clear()');
		this.comboCounter.clear();

		this.resetAll();
	},

	addCounter(callback: CallableFunction, timeMs: number) {
		this.comboCounter.add(callback, timeMs);
	},

	/**
	 * History State has been used so probably just need to clear everything related to transitions:
	 */
	historyStateChange() {
		this.historyStateMode = true;
		this.speedScale = this.historyStateSpeedScale;
		this.clear();
	},

	startNormalTransition() {
		console.trace('Start normal transition');
		this.historyStateMode = false;
		this.speedScale = this.normalSpeedScale;
	},

	introInteractionComplete() {
		this.DOM.addClass(this.$body, 't-intro-interaction-complete');
	},

	showTransitionOverlay(direction = 'up', autoHide = true) {
		this.DOM.addClass(document.querySelector('body'), 'in-transition');
		this.DOM.addClass(this.$overlayBg, '-showing');

		if (direction == 'up') {
			this.DOM.addClass(this.$overlayBg, '-wipeup');
		}

		//If no loading logic then can just hide the loader after about 3s
		if (autoHide) {
			this.hideTransitionOverlay(1000);
		}
	},

	resetAllOtherTileTweens() {},

	hideTransitionOverlay(delay) {
		//Make the overlay active. This will make the logo show etc
		this.comboCounter.add(() => {
			this.DOM.addClass(this.$overlayBg, '-transitionout');
		}, delay * this.speedScale);

		this.comboCounter.add(
			() => {
				this.DOM.removeClass(this.$overlayBg, '-transitionout');
				this.DOM.removeClass(this.$overlayBg, '-showing');
				this.DOM.removeClass(this.$overlayBg, '-wipeup');
			},
			(delay + 1300) * this.speedScale
		);
	},

	homeExploreMouseOver() {
		this.DOM.addClass(this.$body, 'explore-mouse-over');
	},

	homeExploreMouseOut() {
		this.DOM.removeClass(this.$body, 'explore-mouse-over');
	},

	showProjectMenu() {},

	hideProjectMenu() {},

	showNavMenu() {},

	hideNavMenu() {},

	startNavMenuToInfo() {},

	startInfoToHome() {},

	startInfoToNavMenu() {},

	getDepartmentController(department: DepartmentType) {
		if (department == DepartmentType.SCREENS) return this.screensController;
		else if (department == DepartmentType.SPACES) return this.spacesController;
		else if (department == DepartmentType.SYSTEMS) return this.systemsController;
	},

	/**
	 * Start Home to work transition
	 * Scale down + fade BG
	 * Bring in ovelay loader
	 * Reset Home/Pixi whe n hidden
	 * Check Work section loaded (If not then hold)
	 * Hide overlay loader
	 * Show all Work UI and slide projects in
	 *  */
	startHomeToWork(department: DepartmentType) {
		this.comboCounter.clear();
		let newWorkConroller: WorkController = this.getDepartmentController(department);

		this.oldWorkController = this.currentWorkController;
		this.currentWorkController = newWorkConroller;

		this.comboCounter.add(() => {
			(this.uiController as UIController).hideDragPrompt();
		}, 1000);

		//Set all lingering home things:
		this.resetHome();

		//Trigger thge scale up effect on PIXI + container:
		this.DOM.addClass(this.$body, 'transition-home-to-work');
		this.DOM.removeClass(this.$body, 'app-paused');

		//Hide the work container from view, also hide the intro copy that should be hidden on intial slide:
		this.currentWorkController.hideWorkPixiContainer();
		this.DOM.addClass(this.$body, 't-hide-work-intro');

		//Show the overlay:
		this.showTransitionOverlay('down', false);

		this.uiController.hideUi();

		//Reset PIXI + home container sizing when hidden by overlay:
		//Also deactivate home and clear any prompts:
		this.comboCounter.add(() => {
			this.homeController.deactivateForWork();
			this.homeController.clearPromptsOnWayToWork();
		}, 1000 * this.speedScale);

		//After the overlay is fully covered then check if section has fully loaded, or deal with wait:
		this.switchToWorkFromLoader(1000);
	},

	switchToWorkFromLoader(delay: number) {
		//After the overlay is fully covered then check if section has fully loaded, or deal with wait:
		this.comboCounter.add(() => {
			//Loaded, so just move to showing the WORK section:
			if (this.currentWorkController.isFullyLoaded()) {
				this.switchToWorkAfterLoadComplete();
				return;
			}

			//Not loaded, so keep checking loading for new Work Section:
			this.comboCounter.add(() => {
				this.DOM.addClass(this.$body, 't-overlay-loading');
			}, 400 * this.speedScale);

			this.comboCounter.addConditionCheck(
				() => {
					return this.currentWorkController.isFullyLoaded();
				},
				() => {
					this.DOM.removeClass(this.$body, 't-overlay-loading');
					this.currentWorkController.showFromTransitionLoader();
					this.switchToWorkAfterLoadComplete();
				},
				50
			);
		}, delay * this.speedScale);
	},

	/**
	 * After work is loaded then can show
	 * This will fire straight away after if the section is loaded:
	 */
	switchToWorkAfterLoadComplete() {
		//Can hide the overlay and show the Work Pixi container:

		this.DOM.removeClass(this.$body, 't-hide-work-intro');

		this.currentWorkController.showWorkPixiContainer();

		this.hideTransitionOverlay(0);

		this.comboCounter.add(() => {
			this.DOM.removeClass(this.$body, 'transition-home-to-work');
		}, 1000 * this.speedScale);
		//Flag that work is active so can show UI etc:

		this.DOM.addClass(this.$body, '-workactive');

		this.showWorkUITimer(4500);
		this.comboCounter.add(() => {
			this.uiController.showUiItem('backToHome');
			this.uiController.showUi();
		}, 2300);

		//Slide in project from hidden to first item:
		//This will also activate drag controls etc:

		let animateTime = device.innerWidth < Config.mobileBreakpoint ? 1350 : 800;

		this.comboCounter.add(() => {
			this.currentWorkController.animateToFirstProject();
		}, animateTime * this.speedScale);


		//Show Pagination and drag prompt UI:
		this.comboCounter.add(() => this.currentWorkController.showPagination(), 3000 * this.speedScale);
	},

	startWorkToHome() {
		this.resetWork();
		this.comboCounter.clear();

		this.homeController.hidePixiContainer();
		this.DOM.addClass(this.$home, '-active');
		this.DOM.addClass(this.$body, 't-work-to-home');
		this.DOM.removeClass(this.$body, 'work-back-hover');

		//Show the overlay:
		this.showTransitionOverlay('up');

		this.comboCounter.add(() => {
			(this.uiController as UIController).hideDragPrompt();
		}, 1000);

		//Reset work, which will remove the scale affects
		this.comboCounter.add(() => {
			this.uiController.hideUiItem('backToHome');
			this.DOM.addClass(this.$body, 'paging-hide');
			this.homeController.preActivateScenes();
			this.currentWorkController.deactivateWorkSection();
		}, 800 * this.speedScale);

		this.comboCounter.add(() => {
			this.homeController.activateScenes();
		}, 1600 * this.speedScale);

		this.comboCounter.add(() => {
			this.homeController.showDragPrompt();
			this.DOM.removeClass(this.$body, 'paging-hide');
		}, 3000 * this.speedScale);

		this.comboCounter.add(() => {
			this.DOM.removeClass(this.$body, 't-work-to-home');
			this.resetWork();
		}, 1800 * this.speedScale);
	},

	/** Filter Menu has been used on WORK section to cross-link between departments */
	startWorkToWork(toDepartment: DepartmentType, fromDepartment: DepartmentType) {
		console.log('SiteTransitions.startWorkToWork() ');
		this.oldWorkController = this.getDepartmentController(fromDepartment);
		this.currentWorkController = this.getDepartmentController(toDepartment);

		this.currentWorkController.hideForSwitch();
		this.comboCounter.add(() => {
			(this.uiController as UIController).hideDragPrompt();
		}, 1000);

		this.resetWork();
		this.comboCounter.clear();

		this.DOM.addClass(this.$body, 't-work-to-work');

		//Show the overlay:
		this.showTransitionOverlay('up');

		//Reset work, which will remove the scale affects
		this.comboCounter.add(() => {
			this.oldWorkController.deactivateWorkSection();
			this.DOM.addClass(this.$body, 't-hide-work-intro');
		}, 1000 * this.speedScale);

		//Remove the transition:
		this.comboCounter.add(() => {
			this.DOM.removeClass(this.$body, 't-work-to-work');
		}, 1000 * this.speedScale);

		//After the overlay is fully covered then check if section has fully loaded, or deal with wait:
		this.switchToWorkFromLoader(1000);
	},

	showWorkUITimer(timeMs: number) {
		this.comboCounter.add(() => {
			this.DOM.removeClass(this.$body, 'project-active');
			this.DOM.addClass(this.$body, '-workactive');
		}, timeMs * this.speedScale);
	},

	startWorkToProject() {
		this.resetProject();
		this.comboCounter.clear();

		//Set project active status:
		this.DOM.addClass(this.$body, 'project-active');

		this.comboCounter.add(() => {
			(this.uiController as UIController).hideDragPrompt();
		}, 1000);

		//Animate in Project:
		this.animateInDynamicContent();

		//Logo doesn't show, just close button:


		this.uiController.hideUi();
		this.showCloseButton(1000);
	},

	fixCloseShow() {
		this.showCloseButton(1000);
	},

	startProjectToWork(headerHeight: number, activeWorkController: WorkController, targets: any) {
		this.resetProject();
		this.resetWork();

		//If needs to scroll then
		let scrolY: number = this.scrollbarController.getScroll();
		let semiScrolledStyle = scrolY < headerHeight;
		let switchToWorkDelayMs = 0;
		let switchBuffer = semiScrolledStyle ? 0.15 : 0;
		let slideOffContentDelaySecs = 0;
		let slideOffContentTimeSecs = 1 * Config.fromProjectMultipler;

		//Hide the overlay: s
		targets.headerVideoOverlay.style.opacity = 0;

		//Legacy classes??
		this.DOM.removeClass(this.$projectContainer, '-projectactive');

		//If there is any scroll then deal with it:
		//Otherwise start transition back to WORK straight away:
		if (scrolY > 1) {
			let scrollTimeSecs: number =
				(Config.exitProjectScrollTimeBase + scrolY * 0.0001) * Config.exitProjectScrollMuiltplier;
			this.startProjectScrollOff(scrollTimeSecs, activeWorkController);
			switchToWorkDelayMs = (scrollTimeSecs - switchBuffer) * 1000;
			activeWorkController.startExitScroll(semiScrolledStyle);
			slideOffContentDelaySecs = scrollTimeSecs - 0.4;
			slideOffContentTimeSecs = Config.exitMaskTime * Config.fromProjectMultipler + 0.3;
		}

		//Slide off the lower content:
		this.DOM.addClass(this.$body, '-workactive');
		this.slideOffProjectContent(slideOffContentDelaySecs, slideOffContentTimeSecs, activeWorkController);

		//If video if not showing then hard hide
		//If showing then just do a quick fade out:
		if (scrollY > headerHeight) {
			gsap.TweenMax.set(targets.headerVideo, { autoAlpha: 0 });
		} else {
			this.hideProjectHeaderVideo(targets.headerVideo, 0);
		}

		//Remove close button after scroll or soon as clicked if no scroll:
		this.comboCounter.add(() => {
			this.DOM.removeClass(this.$body, 'show-project-close-button');
		}, switchToWorkDelayMs * this.speedScale);


		//Hide the project scrollbar:
		this.comboCounter.add(() => {
			this.scrollbarController.setActive(false);
		}, 700 * this.speedScale);

		//Switch to work and do WORK controller
		this.comboCounter.add(this.switchToWorkFromProject.bind(this), switchToWorkDelayMs * this.speedScale);
	},

	switchToWorkFromProject() {
		//Make call to WORK controller flagging any intial things that need to be triggered:
		this.onStartProjectToWorkSwitch();
	},

	startProjectScrollOff(scrollTime: number, activeWorkController: WorkController) {
		this.DOM.addClass(this.$projectContainer, '-scrollingtohideproject');
		this.projectController.setAllowScrollTweenUpdate(true);
		//Scroll the bar upwards:
		this.isScrollingUp = true;
		this.scrollbarController.tweenTo(
			0,
			scrollTime * this.speedScale,
			Config.exitScrollEase,
			() => {
				this.isScrollingUp = false;
				fastdom.mutate(() => {
					document.documentElement.style.setProperty('--page-progess-deg', `0deg`);
					this.DOM.removeClass(this.$projectContainer, '-scrollingtohideproject');
					this.projectController.setAllowScrollTweenUpdate(false);
				});
			},
			(planeY) => {
				activeWorkController.exitScroll(Math.abs(planeY));
			}
		);
	},

	slideOffProjectContent(delay: number, tweenTime: number, activeWorkController: WorkController) {
		//For some reason the footer is affecting animation because of layering so hide on way out:
		let hideFooterMs = delay == 0 ? 0 : 300;
		this.comboCounter.add(() => {
			this.DOM.removeClass(this.$projectDynamicContent, '-transitioncomplete');
		}, hideFooterMs * this.speedScale);

		if (this.tweens.slideInProjectContent) {
			this.tweens.slideInProjectContent.kill();
			this.tweens.slideInProjectContent = null;
		}

		this.tweens.slideInProjectContent = gsap.TweenMax.to(this.$projectDynamicContent, tweenTime * this.speedScale, {
			y: device.innerHeight - ProjectHeaderSizes.headerHeight() + 10,
			ease: Config.exitMaskEase,
			delay: delay,
			onComplete: () => {
				this.tweens.slideInProjectContent = null;
				this.projectIsHiddenCallback();
				activeWorkController.showDragPromptFromProject();
				this.uiController.showUi();
			}
		});
	},

	showCloseButton(delay) {
		console.log('SHOW CLOSE BUTTON');
		this.comboCounter.add(() => {
			this.$circleCloseProgess.style.setProperty('--page-progess-deg', `0deg`);
			this.DOM.addClass(this.$body, 'show-project-close-button');
		}, delay * this.speedScale);
	},

	activateScrollbar(delay = 0, resetScroll: boolean = true, $content) {
		this.comboCounter.add(() => {
			this.scrollbarController.setFocus($content, 0.4, $content, resetScroll);
			this.scrollbarController.setActive(true);
		}, delay * this.speedScale);
	},

	hideWorkTileUIForProject($title, $circlePulse, $subtitle) {
		this.tweens.workTileUiTitle = gsap.TweenMax.to($title, 0.3 * this.speedScale, {
			autoAlpha: 0,
			ease: Config.linearEase,
			onComplete: () => {
				this.tweens.workTileUiTitle = null;
			}
		});
		this.tweens.workTileUiCircle = gsap.TweenMax.to($circlePulse, 0.18 * this.speedScale, {
			autoAlpha: 0,
			scale: 0,
			ease: Config.linearEase,
			onComplete: () => {
				this.tweens.workTileUiCircle;
			}
		});
		this.tweens.workTileUiSubtitle = gsap.TweenMax.to($subtitle, 0.3 * this.speedScale, {
			autoAlpha: 0,
			y: -10,
			ease: Config.linearEase,
			onComplete: () => {
				this.tweens.workTileUiSubtitle = null;
			}
		});
	},

	showWorkTileUIForProject($title, $circlePulse, $subtitle) {
		this.tweens.workTileUiTitle = gsap.TweenMax.to($title, 0.3 * this.speedScale, {
			autoAlpha: 1,
			delay: 0.7 * this.speedScale,
			ease: Config.linearEase,
			onComplete: () => {
				this.tweens.workTileUiTitle = null;
			}
		});
		this.tweens.workTileUiCircle = gsap.TweenMax.to($circlePulse, 0.6 * this.speedScale, {
			autoAlpha: 1,
			scale: 1,
			delay: 0.7 * this.speedScale,
			ease: gsap.Power0.easeOut,
			onComplete: () => {
				this.tweens.workTileUiCircle;
			}
		});
		this.tweens.workTileUiSubtitle = gsap.TweenMax.to($subtitle, 0.6 * this.speedScale, {
			autoAlpha: 1,
			scale: 1,
			delay: 0.7 * this.speedScale,
			ease: gsap.Power0.easeOut,
			onComplete: () => {
				this.tweens.workTileUiSubtitle = null;
			}
		});
	},

	clearWorkTileParticleTweens() {
		for (let i = 0; i < this.tweens.workTileParticles.length; i++) {
			if (this.tweens.workTileParticles[i]) {
				this.tweens.workTileParticles[i].kill();
				this.tweens.workTileParticles[i] = null;
			}
		}

		this.tweens.workTileParticles = [];
	},

	clearWorkTileTweens() {
		for (let i = 0; i < this.tweens.otherWorkTiles.length; i++) {
			if (this.tweens.otherWorkTiles[i]) {
				this.tweens.otherWorkTiles[i].kill();
				this.tweens.otherWorkTiles[i] = null;
			}
		}

		this.tweens.otherWorkTiles = [];
	},


	animateInDynamicContent() {
		let $dynamicContent = this.projectController.getDynamicContentRef();

		this.DOM.removeClass($dynamicContent, '-transitioncomplete');

		//Animate in content/white bar from bottom:
		this.tweens.slideInProjectContent = gsap.TweenMax.fromTo(
			$dynamicContent,
			Config.toProjectTime * Config.toProjectMultipler * this.speedScale,
			{ y: device.innerHeight - ProjectHeaderSizes.headerHeight() + 10 },
			{
				y: 0,
				ease: Config.toProjectEase,
				delay: 0,
				onComplete: () => {
					this.tweens.slideInProjectContent = null;
					this.DOM.addClass($dynamicContent, '-transitioncomplete');
					this.activateScrollbar(100, true, this.$projectContent);
				}
			}
		);
	},

	showProjectHeaderVideo($headerVideo: HTMLVideoElement, delay = 0, onAnimationComplete: CallableFunction = null) {
		this.kill('fadeOutProjectVideo');

		this.tweens.fadeInProjectVideo = gsap.TweenMax.fromTo(
			$headerVideo,
			0.5 * this.speedScale,
			{ autoAlpha: 0 },
			{
				autoAlpha: 1,
				ease: Config.linearEase,
				delay: delay * this.speedScale,
				onComplete: () => {
					onAnimationComplete();
					this.tweens.fadeInProjectVideo = null;
				}
			}
		);
	},

	hideProjectHeaderVideo($headerVideo: HTMLVideoElement, delay = 0) {
		this.kill('fadeInProjectVideo');

		this.tweens.fadeOutProjectVideo = gsap.TweenMax.to($headerVideo, 0.3 * this.speedScale, {
			autoAlpha: 0,
			ease: Config.linearEase,
			delay: delay,
			onComplete: () => {
				this.tweens.fadeOutProjectVideo = null;
			}
		});
	},

	resetAll() {
		this.resetNavMenu();
		this.resetProjectMenu();
		this.resetInfo();
		this.resetHome();
		this.resetWork();
		this.resetProject();
	},

	resetCommon() {
		this.DOM.removeClass(this.$body, 't-overlay-loading');
	},

	resetNavMenu() {},

	resetProjectMenu() {},

	resetInfo() {},

	resetHome() {
		this.DOM.removeClass(this.$body, 'explore-mouse-over');
		this.DOM.removeClass(this.$body, 'transition-home-to-work');
	},

	resetWork() {
		this.DOM.removeClass(this.$body, 'explore-mouse-over');
		this.DOM.removeClass(this.$body, 't-work-to-home');
		this.DOM.removeClass(this.$body, 't-work-to-work');
		this.DOM.removeClass(this.$body, 't-hide-work-intro');
		this.DOM.removeClass(this.$body, 'work-back-hover');
	},

	resetProject() {
		this.DOM.removeClass(this.$body, 'project-active');

		this.kill('slideInProjectContent');
		this.kill('fadeInProjectVideo');
		this.kill('fadeOutProjectVideo');
		this.kill('tileContainerZoomToProject');
		this.kill('workTileUiTitle');
		this.kill('workTileUiCircle');
		this.kill('workTileUiSubtitle');

		this.kill('workTileImage');
		this.kill('workTileImageRadius');
		this.kill('workTileMask');
		this.kill('workTileSupportElements');
		this.kill('workTileBlackout');
		this.clearWorkTileParticleTweens();
		this.clearWorkTileTweens();

		if (this.isScrollingUp) {
			this.scrollbarController.killScroll();
			this.isScrollingUp = null;
		}
	},

	kill(tween) {
		if (this.tweens[tween]) {
			this.tweens[tween].kill();
			this.tweens[tween] = null;
		}
	}
};
